import { useQuery } from '@apollo/client';
import type {
  WarehouseLocationsQuery,
  WarehouseLocationsQueryVariables,
  WarehousePlacePalletsQuery,
  WarehousePlacePalletsQueryVariables,
} from 'types/graphql';

export const GET_WAREHOUSE_INFO = gql`
  query WarehouseInfoQuery($warehouseId: Int, $placeId: Int) {
    warehouse(warehouseId: $warehouseId, placeId: $placeId) {
      id
      tagKeys
      place {
        id
        description
        fullAddress
				streetAddress1
				streetAddress2
				city
				state
				postalCode
				addressCountry
      }

    }
  }
`;

export function useWarehouseInfo({ warehouseId, placeId }: { warehouseId?: number; placeId?: number }) {
  const { data, loading, error, refetch } = useQuery(GET_WAREHOUSE_INFO, {
    variables: { warehouseId, placeId },
    skip: !placeId && !warehouseId,
  });

  return { warehouse: data?.warehouse, ready: data !== undefined, loading, error, refetch };
}

export const GET_WAREHOUSE_LOCATIONS = gql`
  query WarehouseLocationsQuery($warehouseId: Int, $placeId: Int) {
    warehouseLocations(warehouseId: $warehouseId, placeId: $placeId, take: 5000) {
      id
      slug
      description
      capacity
      tags
    }
  }
`;

export type WarehouseLocation = WarehouseLocationsQuery['warehouseLocations'][number];

export function useWarehouseLocations({ warehouseId, placeId }: { warehouseId?: number; placeId?: number }) {
  const { data, loading, error, refetch } = useQuery<WarehouseLocationsQuery, WarehouseLocationsQueryVariables>(
    GET_WAREHOUSE_LOCATIONS,
    { variables: { warehouseId, placeId }, skip: !warehouseId && !placeId }
  );

  return { locations: data?.warehouseLocations ?? [], ready: data !== undefined, loading, error, refetch };
}

export const GET_WAREHOUSE_PALLETS = gql`
  query WarehousePlacePalletsQuery($placeId: Int!) {
    warehouse(placeId: $placeId) {
      place {
        palletTags(excludeClosedLots: true) {
          id
          tagNumber
          warehouseLocation {
            description
          }
        }
      }
    }
  }
`;

export function useWarehousePalletTags(placeId?: number) {
  const query = useQuery<WarehousePlacePalletsQuery, WarehousePlacePalletsQueryVariables>(GET_WAREHOUSE_PALLETS, {
    variables: { placeId: placeId ?? 0 },
    skip: !placeId,
  });

  return query.data?.warehouse?.place?.palletTags ?? [];
}

export const UPDATE_WAREHOUSE_MUTATION = gql`
	mutation UpdateWarehouse($id: Int!, $input: UpdateWarehouseInput!) {
		updateWarehouse(id: $id, input: $input) {
			id
		}
	}
`;

export const CREATE_WAREHOUSE_LOCATION_MUTATION = gql`
  mutation CreateWarehouseLocation($input: CreateWarehouseLocationInput!) {
    createWarehouseLocation(input: $input) {
      id
      slug
      description
      capacity
      tags
    }
  }
`;

export const UPDATE_WAREHOUSE_LOCATION_MUTATION = gql`
  mutation UpdateWarehouseLocation($id: Int!, $input: UpdateWarehouseLocationInput!) {
    updateWarehouseLocation(id: $id, input: $input) {
      id
      slug
      description
      capacity
      tags
    }
  }
`;
